import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { BRAND, SERVICES, WHY_CHOOSE, PROCESS_STEPS, CITIES, GALLERY } from '@/data/content';
import { BBBSeal, SectionHeading, PortfolioImage } from '@/components/site/SiteBits';
import { Reveal, RevealStagger, RevealItem, MaskedHeading } from '@/components/site/Motion';
import { Seo } from '@/components/site/Seo';
import { EstimateSection } from '@/components/site/EstimateForm';
import { ChatOpenButton } from '@/components/site/ChatWidget';
import FinalCTA from '@/components/site/FinalCTA';

function Icon({ name, ...p }) {
  const Cmp = Icons[name] || Icons.Hammer;
  return <Cmp {...p} />;
}

const HERO_LINES = [
  'Quality home repairs &',
  'residential construction in',
  <><span className="text-brand-maroonBright whitespace-nowrap">Fort Worth,</span> Texas.</>,
];

const CHAPTERS = [
  {
    n: '01',
    text: 'We care about doing the work to help you make your house feel more like a home so you can focus on your life.',
  },
  {
    n: '02',
    text: 'Whether you need a small repair or a larger improvement, the standard stays the same: proper preparation, clean workmanship, honest communication, and a result built to hold up.',
  },
  {
    n: '03',
    text: 'What you pay for with us is better communication, cleaner work, proper prep, honest scope, and fewer headaches. Jobs get planned, discussed, and finished — never left half-done.',
  },
  {
    n: '04',
    text: 'We are BBB accredited. We show up when we say we will. We tell you what we found and what it will take. And we do the parts of the job that don’t show, because those are the parts that make the job last.',
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  // Depth moment: cover drifts slightly slower than the page as you scroll away.
  const coverY = useTransform(scrollYProgress, [0, 1], ['0%', '-8%']);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);

  const [heroMuted, setHeroMuted] = useState(true);
  const heroVideoRef = useRef(null);
  const hasHeroVideo = BRAND.heroVideoSources && BRAND.heroVideoSources.length > 0;

  const toggleHeroSound = () => {
    const v = heroVideoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted) v.play().catch(() => {});
    setHeroMuted(v.muted);
  };

  return (
    <div data-testid="page-home">
      <Seo
        title="Handyman & Home Repair Fort Worth, TX | HearthOak Construction"
        description="Professional home repairs, drywall, painting, door installation and carpentry in Fort Worth and surrounding DFW communities. HearthOak Construction, LLC."
        path="/"
      />
      {/* HERO — full cover photo (all embedded text visible) with CTAs & trust bar below */}
      <section
        ref={heroRef}
        className="relative -mt-[80px] md:-mt-[118px] lg:-mt-[150px] xl:-mt-[168px] flex flex-col overflow-hidden bg-brand-charcoal"
        data-testid="hero-section"
      >
        {/* Cover — video if configured, otherwise the AI cover photo */}
        <div className="relative w-full bg-brand-charcoal overflow-hidden">
          {hasHeroVideo ? (
            <motion.video
              ref={heroVideoRef}
              poster={BRAND.heroVideoPoster || BRAND.coverPhoto}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              aria-label="HearthOak Construction — real patio refresh project walkthrough"
              className="block w-full h-auto max-h-[75vh] object-cover mx-auto"
              style={{ y: coverY }}
              initial={{ opacity: 0, scale: 1.03 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              onLoadedMetadata={(e) => { e.currentTarget.playbackRate = BRAND.heroVideoPlaybackRate || 1; }}
              data-testid="hero-cover-video"
            >
              {BRAND.heroVideoSources.map((s, i) => (
                <source key={i} src={s.src} type={s.type} media={s.media} />
              ))}
            </motion.video>
          ) : (
            <motion.img
              src={BRAND.coverPhoto}
              alt="HearthOak Construction — owner-led home repair and residential construction in Fort Worth and DFW"
              className="block w-full h-auto max-h-[75vh] object-contain mx-auto"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              style={{ y: coverY }}
              initial={{ opacity: 0, scale: 1.03 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              data-testid="hero-cover-photo"
            />
          )}
          {/* Soft top-only scrim so the transparent header is legible without touching the image body */}
          <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-brand-charcoal/80 to-transparent pointer-events-none" />
          {/* Bottom fade so the transition into the dark strip feels intentional */}
          <div className={`absolute inset-x-0 bottom-0 ${hasHeroVideo ? 'h-48 bg-gradient-to-b from-transparent via-brand-charcoal/55 to-brand-charcoal' : 'h-16 bg-gradient-to-b from-transparent to-brand-charcoal'} pointer-events-none`} />

          {/* Overlay text + sound toggle on the hero video */}
          {hasHeroVideo && (
            <>
              <p
                className="absolute inset-x-0 top-20 md:top-40 font-hand text-lg md:text-4xl lg:text-5xl text-brand-bone -rotate-2 text-center drop-shadow-[0_2px_10px_rgba(0,0,0,0.65)] pointer-events-none"
                data-testid="hero-slogan"
              >
                Making Houses Homes.
              </p>
              <div className="absolute left-0 right-0 bottom-0 px-5 md:px-10 pb-4 md:pb-7 pointer-events-none" data-testid="hero-video-overlay">
                <p className="font-serif text-[22px] leading-[1.1] md:text-5xl lg:text-6xl text-brand-bone md:leading-tight drop-shadow-[0_2px_14px_rgba(0,0,0,0.6)]">
                  HearthOak <span className="whitespace-nowrap">Construction, LLC</span>
                </p>
                <p className="mt-1 md:mt-1.5 font-serif text-sm md:text-xl lg:text-2xl text-brand-maroonBright drop-shadow-[0_1px_8px_rgba(0,0,0,0.65)]">
                  Real work. Written estimates. Done right.
                </p>
              </div>
              <button
                type="button"
                onClick={toggleHeroSound}
                className="absolute top-[118px] right-4 bottom-auto md:top-auto md:bottom-7 md:right-10 z-10 flex items-center gap-2 bg-brand-charcoal/70 hover:bg-brand-charcoal/90 backdrop-blur-sm text-brand-bone font-sans text-[10px] md:text-xs uppercase tracking-[0.18em] px-3 py-2 border border-brand-bone/25 transition-colors"
                aria-label={heroMuted ? 'Turn hero video sound on' : 'Turn hero video sound off'}
                data-testid="hero-sound-toggle"
              >
                {heroMuted ? <Icons.VolumeX size={14} /> : <Icons.Volume2 size={14} />}
                {heroMuted ? 'Sound on' : 'Sound off'}
              </button>
            </>
          )}
        </div>

        {/* Dark strip: overlaid message + CTAs + BBB — still within the initial viewport */}
        <div className="surface-dark flex-1 flex items-center">
          <motion.div
            style={{ y: contentY }}
            className="container-x py-8 lg:py-10 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12 items-center w-full"
          >
            <div>
              <motion.div
                className="eyebrow text-brand-maroonBright mb-3"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.05 }}
                data-testid="hero-eyebrow"
              >
                Fort Worth · Burleson · Weatherford · Granbury · Cleburne
              </motion.div>
              <MaskedHeading
                testId="hero-headline"
                lines={HERO_LINES}
                className="font-serif text-2xl sm:text-3xl lg:text-[2.4rem] xl:text-[2.8rem] tracking-tight text-brand-bone leading-[1.3]"
                lineClassName="leading-[1.3]"
              />
              <motion.p
                className="mt-4 font-sans text-sm lg:text-base text-brand-bone/75 max-w-2xl leading-relaxed"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.55 }}
                data-testid="hero-subheadline"
              >
                HearthOak Construction is one trusted contractor for the work that makes your house feel like home — drywall repair, interior and exterior painting, door installation, trim and carpentry, flooring, permanent lighting, and small home repairs for homeowners across Fort Worth, Burleson, Weatherford, Granbury, Cleburne, and surrounding DFW.
              </motion.p>
            </div>
            <motion.div
              className="flex flex-col gap-4"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
            >
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/estimate" className="btn-primary" data-testid="hero-cta-estimate">
                  Request an Estimate
                </Link>
              </div>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-4 pt-4 border-t border-brand-bone/15">
                <BBBSeal size="sm" testId="hero-bbb-seal" />
                <img
                  src="/brand/shawtech-wide-dark.webp"
                  alt="Authorized Installation Partner of ShawTech Lighting"
                  className="h-14 md:h-16 w-auto"
                  loading="lazy"
                  data-testid="hero-shawtech-badge"
                />
                <p className="font-sans text-[11px] md:text-xs tracking-wide text-brand-bone/70 leading-relaxed" data-testid="trust-line">
                  BBB Accredited <span className="text-brand-maroon mx-1">•</span> Owner-led craftsmanship <span className="text-brand-maroon mx-1">•</span> Clear communication
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* MANIFESTO — numbered chapters */}
      <section className="surface-paper border-b border-brand-border" data-testid="manifesto-section">
        <div className="container-x py-20 lg:py-32 grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-14 lg:gap-20">
          <div className="lg:sticky lg:top-32 self-start">
            <Reveal>
              <div className="eyebrow mb-4">Why HearthOak</div>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-brand-charcoal">
                A clear plan.<br />Careful work.<br />A finished result.
              </h2>
              <span className="rule mt-8" />
            </Reveal>
          </div>
          <div>
            {CHAPTERS.map((c, i) => (
              <Reveal
                key={c.n}
                delay={i * 0.05}
                className={`grid grid-cols-[auto_1fr] gap-6 md:gap-10 py-8 ${i > 0 ? 'border-t border-brand-border' : ''}`}
                data-testid={`chapter-${c.n}`}
              >
                <div className="font-serif text-3xl md:text-4xl text-brand-maroon leading-none pt-1">{c.n}</div>
                <p className="font-sans text-base lg:text-lg leading-relaxed text-brand-charcoal/80">{c.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CORE SERVICES */}
      <section className="border-b border-brand-border">
        <div className="container-x py-20 lg:py-28">
          <Reveal>
            <SectionHeading eyebrow="What We Do" title="Core services" kicker="Six things we do well — done in a way that respects the home and the homeowner." />
          </Reveal>
          <RevealStagger className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-l border-t border-brand-border" data-testid="services-grid">
            {SERVICES.map((s) => (
              <RevealItem key={s.slug}>
                <Link
                  to={`/services/${s.slug}`}
                  className="group flex flex-col h-full p-8 lg:p-10 border-r border-b border-brand-border bg-brand-bone hover:bg-brand-boneAlt transition-colors duration-300"
                  data-testid={`service-card-${s.slug}`}
                >
                  <Icon name={s.icon} size={28} strokeWidth={1.5} className="text-brand-maroon transition-transform duration-300 group-hover:-translate-y-1" />
                  <h3 className="mt-6 font-serif text-2xl text-brand-charcoal">{s.title}</h3>
                  <p className="mt-3 font-sans text-sm text-brand-muted leading-relaxed">{s.short}</p>
                  <div className="mt-6 font-sans text-xs tracking-[0.22em] uppercase text-brand-maroon inline-flex items-center gap-2">
                    Learn more <Icons.ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                  </div>
                </Link>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="surface-paper border-b border-brand-border">
        <div className="container-x py-20 lg:py-28">
          <Reveal>
            <SectionHeading eyebrow="The Difference" title="What makes us different." />
          </Reveal>
          <RevealStagger className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" stagger={0.06}>
            {WHY_CHOOSE.map((w) => (
              <RevealItem
                key={w.title}
                className="bg-brand-bone border border-brand-border p-7 transition-colors duration-300 hover:border-brand-maroon/50"
                data-testid={`why-${w.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon name={w.icon} size={22} strokeWidth={1.5} className="text-brand-maroon" />
                <h3 className="mt-4 font-serif text-xl text-brand-charcoal">{w.title}</h3>
                <p className="mt-2 font-sans text-sm text-brand-muted leading-relaxed">{w.text}</p>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* RECENT WORK */}
      <section className="border-b border-brand-border">
        <div className="container-x py-20 lg:py-28">
          <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <SectionHeading eyebrow="Recent Work" title="Real projects, real photos." kicker="A few recent jobs across doors, drywall, and finish work." />
            <Link to="/gallery" className="btn-secondary self-start md:self-end" data-testid="recent-work-view-all">View full gallery</Link>
          </Reveal>
          <RevealStagger className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" stagger={0.09} data-testid="recent-work-grid">
            {GALLERY.slice(0, 4).map((g) => (
              <RevealItem key={g.id}>
                <div className="group relative border border-brand-border bg-brand-bone overflow-hidden transition-shadow duration-500 hover:shadow-[0_18px_40px_-20px_rgba(28,28,29,0.55)]">
                  <div className="overflow-hidden">
                    <PortfolioImage
                      src={g.src}
                      className="aspect-[4/5] [&_img]:transition-transform [&_img]:duration-[900ms] [&_img]:ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:[&_img]:scale-[1.06]"
                      alt={g.title}
                    />
                    {/* Spotlight sweep on hover */}
                    <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-t from-brand-charcoal/40 via-transparent to-transparent" />
                    {/* Corner frame accent */}
                    <span className="pointer-events-none absolute left-3 top-3 h-6 w-6 border-l-2 border-t-2 border-brand-bone/0 group-hover:border-brand-bone/80 transition-colors duration-500" />
                    <span className="pointer-events-none absolute right-3 bottom-[86px] h-6 w-6 border-r-2 border-b-2 border-brand-bone/0 group-hover:border-brand-bone/80 transition-colors duration-500" />
                  </div>
                  <div className="p-4">
                    <div className="text-[10px] tracking-[0.22em] uppercase text-brand-maroon">{g.category}</div>
                    <div className="mt-1 font-serif text-base text-brand-charcoal leading-snug">{g.title}</div>
                  </div>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* PROCESS */}
      <section className="surface-paper border-b border-brand-border">
        <div className="container-x py-20 lg:py-28">
          <Reveal>
            <SectionHeading eyebrow="Our Process" title="Straightforward, from first message to last coat." />
          </Reveal>
          <RevealStagger className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 border-t border-l border-brand-border" stagger={0.07}>
            {PROCESS_STEPS.map((s) => (
              <RevealItem key={s.n} className="h-full">
                <div className="h-full p-7 border-r border-b border-brand-border bg-brand-bone" data-testid={`process-step-${s.n}`}>
                  <div className="font-serif text-4xl text-brand-maroon">{s.n}</div>
                  <div className="mt-3 font-sans text-sm font-semibold uppercase tracking-wider text-brand-charcoal">{s.title}</div>
                  <p className="mt-2 font-sans text-sm text-brand-muted leading-relaxed">{s.text}</p>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="border-b border-brand-border">
        <div className="container-x py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-12">
          <Reveal>
            <div className="eyebrow mb-3">Service Area</div>
            <h2 className="h2">Local to Burleson. Working across DFW.</h2>
            <span className="rule mt-6" />
            <p className="body-lg mt-6 max-w-md">
              We work with homeowners and property managers across Fort Worth, Burleson, Weatherford, Granbury, Cleburne, and the surrounding DFW area.
            </p>
            <Link to="/service-areas" className="btn-secondary mt-8 inline-flex" data-testid="home-service-area-cta">See all service areas</Link>
          </Reveal>
          <RevealStagger className="grid grid-cols-2 sm:grid-cols-3 gap-3" stagger={0.05}>
            {CITIES.map((c) => (
              <RevealItem key={c.slug} className="h-full">
                <Link
                  to={c.intro ? `/${c.slug}` : '/service-areas'}
                  className="group flex flex-col h-full p-5 border border-brand-border bg-brand-bone hover:bg-brand-boneAlt hover:border-brand-maroon/40 transition-colors duration-300"
                  data-testid={`home-city-${c.slug}`}
                >
                  <Icons.MapPin size={16} className="text-brand-maroon transition-transform duration-300 group-hover:-translate-y-0.5" />
                  <div className="mt-3 font-serif text-lg text-brand-charcoal">{c.name}</div>
                </Link>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* QUICK CHAT CTA */}
      <section className="surface-dark border-b border-brand-borderDark" data-testid="chat-cta-section">
        <div className="container-x py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-10 lg:gap-16 items-center">
          <Reveal>
            <div className="eyebrow text-brand-maroonBright mb-3">Talk to us in under 60 seconds</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-brand-bone">
              Not sure where to start?<br />
              <span className="text-brand-maroonBright">Chat with us.</span>
            </h2>
            <p className="mt-6 font-sans text-base lg:text-lg text-brand-bone/75 max-w-xl leading-relaxed">
              A quick back-and-forth, no forms to fill. Tell us what you need and we'll get the right details to the owner — he reaches out personally within 24 hours.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="flex flex-col gap-3">
            <ChatOpenButton
              className="btn-primary w-full justify-center text-center"
              label="Start a chat"
              testId="home-chat-open"
            />
          </Reveal>
        </div>
      </section>

      <EstimateSection
        testIdPrefix="home-estimate"
        anchorId="estimate"
        heading="Let us know how we can help."
        headingClass="text-brand-maroonDark"
        eyebrow="Request an Estimate"
      />

      <FinalCTA />
    </div>
  );
}
